import React from 'react';
import { KeyboardArrowDown, ErrorOutline } from '@mui/icons-material';
import Box from '@mui/material/Box';
import FormControl from '@mui/material/FormControl';
import FormHelperText from '@mui/material/FormHelperText';
import FormLabel from '@mui/material/FormLabel';
import InputAdornment from '@mui/material/InputAdornment';
import type { SelectProps as MuiSelectProps } from '@mui/material/Select';
import { Typography } from '../Typography';
import { StyledSelect } from './Select.styled';

export type SelectProps = Omit<MuiSelectProps, 'variant' | 'hiddenLabel'> & {
  label?: string;
  helperText?: string;
  size?: 'small' | 'medium';
  inlineLabel?: boolean;
  leftSlot?: React.ReactNode;
};

/**
 * Renders a Select component with an external label
 */
export const Select: React.FC<React.PropsWithChildren<SelectProps>> = ({
  label,
  helperText,
  size = 'medium',
  error,
  required,
  inlineLabel = false,
  leftSlot,
  ...props
}) => {
  // Custom endAdornment that shows error icon + chevron when error is true
  const customEndAdornment = error ? (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: '4px',
        mr: '16px',
        backgroundColor: props.disabled ? '#F2F4F7' : '#fff',
      }}
    >
      <ErrorOutline sx={{ color: '#F04438', fontSize: '20px' }} />
    </Box>
  ) : undefined;

  // Custom startAdornment for inline label and left slot
  const customStartAdornment =
    (inlineLabel && label) || leftSlot ? (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignContent: 'center',
          borderRadius: '8px 0 0 8px',
          height: size === 'small' ? '39px' : '47px',
          marginLeft: 0,
          pl: 2,
          marginRight: 0,
          backgroundColor: props.disabled ? '#F2F4F7' : '#ffffff',
        }}
      >
        <InputAdornment position="start">
          {leftSlot}
          {inlineLabel && label && (
            <FormLabel htmlFor={props.id}>
              <Typography variant="body1" sx={{ color: '#414651' }}>
                {label}:
              </Typography>
            </FormLabel>
          )}
        </InputAdornment>
      </Box>
    ) : undefined;

  return (
    <Box sx={{ width: props.fullWidth ? '100%' : 'auto' }}>
      {label && !inlineLabel && (
        <FormLabel
          sx={{
            ml: '2px',
            mb: '4px',
            display: 'block',
            fontSize: '14px',
            fontWeight: '500',
            lineHeight: '20px',
            color: '#344054',
          }}
          htmlFor={props.id}
          error={error}
          required={required}
        >
          {label}
        </FormLabel>
      )}
      <FormControl fullWidth={props.fullWidth} error={error} sx={{ m: 0 }}>
        <StyledSelect
          {...props}
          inputProps={{
            'aria-label': label,
          }}
          size={size}
          displayEmpty
          IconComponent={KeyboardArrowDown}
          startAdornment={customStartAdornment}
          endAdornment={
            <>
              {props.endAdornment}
              {customEndAdornment}
            </>
          }
          sx={{
            borderRadius: '8px',
            ...(inlineLabel && {
              '& .MuiSelect-select': {
                fontWeight: '500',
              },
            }),
            ...(props.sx || {}),
          }}
          MenuProps={{
            disableScrollLock: true,
            ...props.MenuProps,
          }}
        />
        {helperText && <FormHelperText sx={{ ml: '2px' }}>{helperText}</FormHelperText>}
      </FormControl>
    </Box>
  );
};
